import { useModalActions } from '@carboncopyco/components';
import { useSet } from '@uidotdev/usehooks';
import constate from 'constate';
import { useCallback } from 'react';
import {
  EVENT_MAIN_TO_APP_ANALYTICS_SEESION_IDLE,
  SESSION_IDLE_MESSAGE_TYPE,
} from '../../../shared/constants/analytics-consts';
import useElectronListener from '../../hooks/useElectronListener';
import { useTabManager } from '../../services/TabManager';
import { useTabs } from './TabsContext';

function useAnalyticsSessionContext() {
  const { activeGlobalTab, getGroupMainTab } = useTabs();
  const idleSessions = useSet<string>([]);
  const { closeGroupTabs } = useTabManager();
  const { set: modalsSet } = useModalActions();
  const isAnyGlobalModalOpen = Array.from(modalsSet).length > 0;

  const currentTabAnalyticsSessionId = activeGlobalTab?.analyticsSessionId;

  useElectronListener<{
    analyticsSessionId: string;
    type: string;
  }>(
    EVENT_MAIN_TO_APP_ANALYTICS_SEESION_IDLE,
    (message) => {
      if (message.type === SESSION_IDLE_MESSAGE_TYPE) {
        idleSessions.add(message.analyticsSessionId);
      }
    },
    [currentTabAnalyticsSessionId]
  );
  const isSessionIdle =
    !isAnyGlobalModalOpen &&
    !!currentTabAnalyticsSessionId &&
    idleSessions.has(currentTabAnalyticsSessionId);

  const onResume = useCallback(() => {
    if (isSessionIdle) {
      idleSessions.delete(currentTabAnalyticsSessionId);
      window.dsDesktop.analyticsClient.resume(
        currentTabAnalyticsSessionId,
        activeGlobalTab?.type
      );
    }
  }, [isSessionIdle]);

  const onDestroy = useCallback(() => {
    if (!isSessionIdle) {
      return;
    }
    idleSessions.delete(currentTabAnalyticsSessionId);
    const groupMainTab = getGroupMainTab(activeGlobalTab);
    if (groupMainTab) {
      closeGroupTabs(groupMainTab);
    }
  }, [isSessionIdle]);

  return {
    closeSpeakerPanel: () =>
      window.dsDesktop.speakerPanel.closeFromSpeakerPanelIfOpen({
        resetData: true,
      }),
    analyticsSessionId: currentTabAnalyticsSessionId,
    isSessionIdle,
    forClient:
      activeGlobalTab?.forClient ??
      activeGlobalTab?.payload?.play?.forClient ??
      activeGlobalTab?.payload?.presentation?.forClient,
    tab: activeGlobalTab,
    onResume,
    onDestroy,
  };
}

export const [AnalyticsSessionProvider, useAnalyticsSession] = constate(
  useAnalyticsSessionContext,
  (value) => value
);
